import { supabase } from '../supabaseClient';
import { UserProfile } from '../types';

// Slug validation matches the Postgres CHECK constraint on user_profiles.slug:
//   lowercase letters, digits, hyphens; 2-40 chars; must start with alnum.
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,39}$/;

export function normalizeSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function isValidSlug(input: string): boolean {
  return SLUG_PATTERN.test(input);
}

export async function fetchMyProfile(): Promise<{ data: UserProfile | null; error: string | null }> {
  const { data: session } = await supabase.auth.getSession();
  const uid = session.session?.user.id;
  if (!uid) return { data: null, error: 'Not signed in' };
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('user_id', uid)
    .maybeSingle();
  if (error) return { data: null, error: error.message };
  return { data: data as UserProfile | null, error: null };
}

export async function upsertMyProfile(
  patch: Partial<Omit<UserProfile, 'user_id' | 'created_at' | 'updated_at'>>,
): Promise<{ data: UserProfile | null; error: string | null }> {
  const { data: session } = await supabase.auth.getSession();
  const uid = session.session?.user.id;
  if (!uid) return { data: null, error: 'Not signed in' };

  // Try update first; if no row, insert with the patch.
  const { data: existing } = await supabase
    .from('user_profiles')
    .select('user_id')
    .eq('user_id', uid)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from('user_profiles')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('user_id', uid)
      .select()
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as UserProfile, error: null };
  }

  // Insert requires slug; caller must provide it on first save.
  if (!patch.slug) return { data: null, error: 'Slug required for first profile save' };
  const { data, error } = await supabase
    .from('user_profiles')
    .insert({ user_id: uid, ...patch })
    .select()
    .single();
  if (error) return { data: null, error: error.message };
  return { data: data as UserProfile, error: null };
}

// Check slug availability. Returns { available: boolean, error }.
// A slug is "available" if no OTHER user's profile has it.
export async function isSlugAvailable(slug: string): Promise<{ available: boolean; error: string | null }> {
  const { data: session } = await supabase.auth.getSession();
  const uid = session.session?.user.id;
  if (!uid) return { available: false, error: 'Not signed in' };
  const { data, error } = await supabase
    .from('user_profiles')
    .select('user_id')
    .eq('slug', slug)
    .neq('user_id', uid)
    .maybeSingle();
  if (error) return { available: false, error: error.message };
  return { available: !data, error: null };
}
